import { reportError } from "@/utils/reportError";
import { trackProfileEdited } from '@/utils/analytics';
import SelectionModal from "@/components/SelectionModal";
import { GENEVA_POSTAL_CODES } from "@/data/postalCodes";
import { OTHER_CANTONS } from "@/data/cantons";
import { useCompanies } from "@/hooks/useCompanies";
import { useProfile } from "@/contexts/ProfileContext";
import { supabase } from "@/utils/supabase";
import { getAuthRedirectUrl } from "@/utils/authRedirect";
import { Ionicons } from "@expo/vector-icons";
import { Image } from "expo-image";
import * as ImagePicker from "expo-image-picker";
import { LinearGradient } from "expo-linear-gradient";
import { Stack, useRouter } from "expo-router";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    ActivityIndicator,
    Alert,
    ScrollView,
    StatusBar,
    StyleSheet,
    Text,
    TextInput,
    TouchableOpacity,
    View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

export default function EditProfileScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const { profile, loading: profileLoading, updateProfile } = useProfile();
  const { companies, addCompany } = useCompanies();

  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [postalCode, setPostalCode] = useState("");
  
  const [postalCodeModalVisible, setPostalCodeModalVisible] = useState(false);
  const [companyModalVisible, setCompanyModalVisible] = useState(false);
  const [saving, setSaving] = useState(false);

  // Sync with profile when it loads
  useEffect(() => {
    if (profile) {
      setFirstName(profile.firstName);
      setLastName(profile.lastName);
      setEmail(profile.email);
      setCompany(profile.company || "");
      setCompanyId(profile.companyId || null);
      setPostalCode(profile.postalCode);
      if (profile.avatarUrl) {
        setProfileImage(profile.avatarUrl);
      }
    }
  }, [profile]);

  const uploadAvatar = async (uri: string): Promise<string | null> => {
    try {
      const userId = profile?.id;
      if (!userId) return null;

      // Whitelist d'extensions image acceptées. Pas de SVG (vecteur XSS),
      // pas de GIF (taille + animation peu utile pour un avatar).
      const ALLOWED_MIME: Record<string, string> = {
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
        webp: 'image/webp',
      };
      const MAX_BYTES = 5 * 1024 * 1024; // 5 MB

      const rawExt = uri.split('.').pop()?.toLowerCase().split('?')[0] ?? '';
      const contentType = ALLOWED_MIME[rawExt];
      if (!contentType) {
        Alert.alert(
          t('editProfile.unsupportedFormatTitle'),
          t('editProfile.unsupportedFormatMessage')
        );
        return null;
      }

      const response = await fetch(uri);
      const blob = await response.blob();
      const arrayBuffer = await new Response(blob).arrayBuffer();

      if (arrayBuffer.byteLength > MAX_BYTES) {
        Alert.alert(
          t('editProfile.imageTooLargeTitle'),
          t('editProfile.imageTooLargeMessage')
        );
        return null;
      }

      // Chemin FIXE par utilisateur : chaque upload écrase le précédent au
      // lieu d'accumuler des fichiers horodatés publics à vie. Le cache CDN
      // est contourné par le query param ?v= ajouté à l'URL stockée.
      const fileName = `avatar.${rawExt === 'jpeg' ? 'jpg' : rawExt}`;
      const filePath = `${userId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, arrayBuffer, {
          contentType,
          upsert: true,
        });

      if (uploadError) {
        reportError(uploadError);
        Alert.alert(t('common.errorTitle'), t('editProfile.uploadError'));
        return null;
      }

      // Purge best-effort des anciens fichiers (noms horodatés historiques ou
      // autre extension) — policy DELETE scopée au dossier de l'utilisateur.
      try {
        const { data: existing } = await supabase.storage.from('avatars').list(userId);
        const stale = (existing ?? [])
          .filter((f) => f.name !== fileName)
          .map((f) => `${userId}/${f.name}`);
        if (stale.length > 0) {
          await supabase.storage.from('avatars').remove(stale);
        }
      } catch (cleanupError) {
        reportError(cleanupError);
      }

      const { data } = supabase.storage.from('avatars').getPublicUrl(filePath);
      return `${data.publicUrl}?v=${Date.now()}`;
    } catch (error) {
      reportError(error);
      Alert.alert(t('common.errorTitle'), t('editProfile.uploadGenericError'));
      return null;
    }
  };

  const pickImage = async () => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [1, 1],
      quality: 0.8,
    });

    if (!result.canceled) {
      const localUri = result.assets[0].uri;

      // Show preview immediately
      setProfileImage(localUri);

      // Upload in background. On bloque l'enregistrement tant que l'upload
      // n'est pas terminé (sinon le garde-fou au save retomberait sur
      // l'ancien avatar et la nouvelle photo serait perdue).
      setUploadingAvatar(true);
      const publicUrl = await uploadAvatar(localUri);
      setUploadingAvatar(false);
      // En cas d'échec, on revient à l'ancien avatar : sinon l'URI locale
      // file:// resterait dans le state et serait persistée au save (photo
      // morte au prochain démarrage / sur les autres appareils).
      setProfileImage(publicUrl ?? profile?.avatarUrl ?? null);
    }
  };

  const handlePostalCodeSelect = (value: string) => {
    setPostalCode(value);
  };

  // Sélection d'une entreprise existante dans la liste.
  const handleCompanySelect = (name: string) => {
    setCompany(name);
    const match = companies.find(
      (c) => c.name.trim().toLowerCase() === name.trim().toLowerCase()
    );
    setCompanyId(match?.id ?? null);
  };

  // Ajout d'une nouvelle entreprise saisie dans la recherche : insert (dédup
  // insensible à la casse) puis on lie le company_id retourné.
  const handleCompanyAdd = async (name: string) => {
    const created = await addCompany(name);
    if (created) {
      setCompany(created.name);
      setCompanyId(created.id);
    } else {
      // Fallback : on garde le nom saisi en texte libre si l'insert échoue,
      // sans bloquer l'utilisateur (le classement entreprise ne sera pas lié).
      setCompany(name.trim());
      setCompanyId(null);
      Alert.alert(t("common.errorTitle"), t("editProfile.companyAddError"));
    }
  };

  const handleSave = async () => {
    // Validate required fields
    if (!firstName.trim() || !lastName.trim() || !postalCode.trim()) {
      Alert.alert(t("common.errorTitle"), t("editProfile.fillRequiredFields"));
      return;
    }

    // L'upload de l'avatar n'est pas terminé : on attend, sinon la nouvelle
    // photo serait perdue (le garde-fou retomberait sur l'ancien avatar).
    if (uploadingAvatar) {
      Alert.alert(t("editProfile.photoUploadingTitle"), t("editProfile.photoUploadingMessage"));
      return;
    }

    setSaving(true);

    try {
      // Update email if changed. Le message de confirmation d'email est fusionné
      // dans l'alerte de succès finale (une seule alerte, pas deux empilées).
      const emailChanged = email !== profile?.email;
      if (emailChanged) {
        const { error: emailError } = await supabase.auth.updateUser(
          { email },
          { emailRedirectTo: getAuthRedirectUrl('/confirmation') }
        );

        if (emailError) {
          Alert.alert(
            t("common.errorTitle"),
            t("editProfile.emailUpdateError", { message: emailError.message })
          );
          setSaving(false);
          return;
        }
      }

      // On ne persiste qu'une URL distante uploadée. Si l'upload a échoué ou
      // n'est pas terminé, `profileImage` peut être une URI locale
      // (file://, content://, ph://) qui ne s'affiche sur aucun autre appareil
      // — on retombe alors sur l'avatar existant pour ne jamais écrire d'URI
      // morte en base (cf. bug "l'image ne s'affiche pas").
      const persistableAvatar = profileImage && /^https?:\/\//.test(profileImage)
        ? profileImage
        : profile?.avatarUrl ?? null;

      // Update profile data
      const result = await updateProfile({
        firstName: firstName,
        lastName: lastName,
        company: company || null,
        companyId: companyId,
        postalCode: postalCode,
        avatarUrl: persistableAvatar,
      });

      if (result.success) {
        const editedFields: string[] = [];
        if (firstName !== profile?.firstName) editedFields.push('firstName');
        if (lastName !== profile?.lastName) editedFields.push('lastName');
        if (company !== (profile?.company || '')) editedFields.push('company');
        if (postalCode !== profile?.postalCode) editedFields.push('postalCode');
        if (profileImage !== profile?.avatarUrl) editedFields.push('avatar');
        if (emailChanged) editedFields.push('email');
        if (editedFields.length > 0) trackProfileEdited(editedFields);

        Alert.alert(
          t("common.successTitle"),
          emailChanged
            ? t("editProfile.profileUpdatedWithEmail")
            : t("editProfile.profileUpdated"),
          [{ text: t("common.ok"), onPress: () => router.back() }]
        );
      } else {
        Alert.alert(t("common.errorTitle"), t("editProfile.profileUpdateError"));
      }
    } catch (error: any) {
      reportError(error);
      Alert.alert(t("common.errorTitle"), t("editProfile.genericError"));
    } finally {
      setSaving(false);
    }
  };

  if (profileLoading && !profile) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: "#FBEFE3" }} edges={["top"]}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#CD7728" />
          <Text style={styles.loadingText}>{t("common.loading")}</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      <SafeAreaView
        style={{ flex: 1, backgroundColor: "#FBEFE3" }}
        edges={["top"]}
      >
        <StatusBar barStyle="dark-content" backgroundColor="#FBEFE3" />
        <LinearGradient
          colors={["#FBEFE3", "#FFFFFF"]}
          locations={[0, 1]}
          style={styles.container}
        >
          <ScrollView
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
          >
            {/* Header */}
            <View style={styles.header}>
              <TouchableOpacity
                style={styles.backButton}
                onPress={() => router.back()}
              >
                <Ionicons name="chevron-back" size={24} color="#000" />
              </TouchableOpacity>
              <Text style={styles.headerTitle}>{t("editProfile.headerTitle")}</Text>
              <View style={{ width: 40 }} />
            </View>

            {/* Profile Image */}
            <TouchableOpacity
              onPress={pickImage}
              style={styles.profileImageContainer}
            >
              {profileImage ? (
                <Image
                  source={{ uri: profileImage }}
                  style={styles.profileImage}
                  cachePolicy="memory-disk"
                  contentFit="cover"
                  transition={200}
                />
              ) : (
                <View style={styles.profileImagePlaceholder}>
                  <Ionicons name="person" size={60} color="#999" />
                </View>
              )}
              <View style={styles.editBadge}>
                <Ionicons name="camera" size={16} color="#FFF" />
              </View>
            </TouchableOpacity>

            <Text style={styles.imageHint}>
              {t("editProfile.imageHint")}
            </Text>

            {/* Form Fields */}
            <View style={styles.form}>
              <Text style={styles.label}>{t("editProfile.firstNameLabel")}</Text>
              <TextInput
                style={styles.input}
                value={firstName}
                onChangeText={setFirstName}
                placeholder={t("editProfile.firstNamePlaceholder")}
                placeholderTextColor="#999"
              />

              <Text style={styles.label}>{t("editProfile.lastNameLabel")}</Text>
              <TextInput
                style={styles.input}
                value={lastName}
                onChangeText={setLastName}
                placeholder={t("editProfile.lastNamePlaceholder")}
                placeholderTextColor="#999"
              />

              <Text style={styles.label}>{t("editProfile.emailLabel")}</Text>
              <TextInput
                style={styles.input}
                value={email}
                onChangeText={setEmail}
                placeholder={t("editProfile.emailPlaceholder")}
                placeholderTextColor="#999"
                keyboardType="email-address"
                autoCapitalize="none"
              />
              <Text style={styles.hint}>
                {t("editProfile.emailHint")}
              </Text>

              <Text style={styles.label}>{t("editProfile.companyLabel")}</Text>
              <TouchableOpacity
                style={styles.selectInput}
                onPress={() => setCompanyModalVisible(true)}
              >
                <Text
                  style={[
                    styles.selectInputText,
                    company ? styles.selectInputTextSelected : {},
                  ]}
                >
                  {company || t("editProfile.companyPlaceholder")}
                </Text>
                <Ionicons name="chevron-down" size={20} color="#999" />
              </TouchableOpacity>

              <Text style={styles.label}>{t("editProfile.postalCodeLabel")}</Text>
              <TouchableOpacity
                style={styles.selectInput}
                onPress={() => setPostalCodeModalVisible(true)}
              >
                <Text
                  style={[
                    styles.selectInputText,
                    postalCode ? styles.selectInputTextSelected : {},
                  ]}
                >
                  {postalCode || t("editProfile.selectPostalCode")}
                </Text>
                <Ionicons name="chevron-down" size={20} color="#999" />
              </TouchableOpacity>
            </View>

            {/* Save Button */}
            <TouchableOpacity
              style={styles.saveButton}
              onPress={handleSave}
              disabled={saving}
            >
              <LinearGradient
                colors={["#1C453F", "#78A584"]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={styles.saveButtonGradient}
              >
                {saving ? (
                  <ActivityIndicator color="#FFF" />
                ) : (
                  <Text style={styles.saveButtonText}>{t("editProfile.saveButton")}</Text>
                )}
              </LinearGradient>
            </TouchableOpacity>
          </ScrollView>

          <SelectionModal
            visible={postalCodeModalVisible}
            title={t("editProfile.selectPostalCode")}
            data={GENEVA_POSTAL_CODES}
            selectedValue={postalCode}
            onSelect={handlePostalCodeSelect}
            onClose={() => setPostalCodeModalVisible(false)}
            searchable
            searchPlaceholder={t("editProfile.searchPostalCodePlaceholder")}
            secondaryData={OTHER_CANTONS}
            secondaryTitle={t("editProfile.otherCantonTitle")}
            emptyText={t("editProfile.noPostalCodeFound")}
          />

          <SelectionModal
            visible={companyModalVisible}
            title={t("editProfile.selectCompanyTitle")}
            data={companies.map((c) => c.name)}
            selectedValue={company}
            onSelect={handleCompanySelect}
            onClose={() => setCompanyModalVisible(false)}
            searchable
            searchPlaceholder={t("editProfile.searchCompanyPlaceholder")}
            onAddOption={handleCompanyAdd}
            addOptionLabel={(q) => t("editProfile.addCompany", { query: q })}
            emptyText={t("editProfile.noCompanyFound")}
          />
        </LinearGradient>
      </SafeAreaView>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    padding: 24,
    paddingTop: 0,
    paddingBottom: 40,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  loadingText: {
    fontFamily: "Inter_400Regular",
    fontSize: 16,
    color: "#666",
    marginTop: 12,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 32,
  },
  backButton: {
    width: 40,
    height: 40,
    alignItems: "center",
    justifyContent: "center",
  },
  headerTitle: {
    fontFamily: "Inter_700Bold",
    fontSize: 18,
    color: "#000",
    flex: 1,
    textAlign: "center",
  },
  profileImageContainer: {
    alignSelf: "center",
    position: "relative",
    marginBottom: 8,
  },
  profileImage: {
    width: 100,
    height: 100,
    borderRadius: 60,
  },
  profileImagePlaceholder: {
    width: 100,
    height: 100,
    borderRadius: 60,
    backgroundColor: "#F0F0F0",
    alignItems: "center",
    justifyContent: "center",
  },
  editBadge: {
    position: "absolute",
    bottom: 0,
    right: 0,
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: "#CD7728",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 3,
    borderColor: "#FFF",
  },
  imageHint: {
    fontFamily: "Inter_400Regular",
    fontSize: 13,
    color: "#999",
    textAlign: "center",
    marginBottom: 12,
  },
  form: {
    marginBottom: 12,
  },
  label: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 14,
    color: "#000",
    marginBottom: 8,
    marginTop: 16,
  },
  input: {
    backgroundColor: "#fff",
    borderRadius: 12,
    paddingVertical: 14,
    paddingHorizontal: 16,
    fontFamily: "Inter_400Regular",
    fontSize: 16,
    color: "#000",
       shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  hint: {
    fontFamily: "Inter_400Regular",
    fontSize: 12,
    color: "#999",
    marginTop: 4,
  },
  selectInput: {
    backgroundColor: "#fff",
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 14,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  selectInputText: {
    fontFamily: "Inter_400Regular",
    fontSize: 16,
    color: "#999",
  },
  selectInputTextSelected: {
    color: "#000",
  },
  saveButton: {
    marginTop: 16,
  },
  saveButtonGradient: {
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: "center",
  },
  saveButtonText: {
    fontFamily: "Inter_700Bold",
    fontSize: 16,
    color: "#FFF",
  },
  fullModalContainer: {
    flex: 1,
    backgroundColor: '#FFF',
  },
  fullModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
  },
  fullModalTitle: {
    fontFamily: "Inter_700Bold",
    fontSize: 20,
    color: "#000",
  },
  modalItem: {
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#F0F0F0",
  },
  modalItemText: {
    fontFamily: "Inter_400Regular",
    fontSize: 16,
    color: "#333",
  },
});
